import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2, Download, Loader2, Captions, Clapperboard } from "lucide-react";
import { api, APP_ORIGIN } from "@/lib/api";
import StatusPill from "@/components/ui/StatusPill";
import { downloadBlob } from "@/components/riptag/util";
import { fmtTime, pollJob, FORMAT_LABEL } from "./snippetUtil";
import { appConfirm } from "@/components/AppDialogHost";

const CLIP_STATUS = { cutting: "pending", cut: "draft", rendering: "pending", rendered: "published", failed: "failed" };

export function ClipCard({ clip, templates, renderCost, onChanged }) {
  const [title, setTitle] = useState(clip.title);
  const [fmts, setFmts] = useState({ vertical: true, square: false, wide: false });
  const [rendering, setRendering] = useState(clip.status === "rendering");
  const [renderProg, setRenderProg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSubs, setShowSubs] = useState(false);
  const [subs, setSubs] = useState(null);
  const [trim, setTrim] = useState({ start: clip.start, end: clip.end });
  const [downloading, setDownloading] = useState("");

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      await api.patch(`/snippets/clips/${clip.clip_id}`, body);
      if (okMsg) toast.success(okMsg);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const render = async () => {
    const formats = Object.keys(fmts).filter((k) => fmts[k]);
    if (!formats.length) return toast.error("Pick at least one format");
    setRendering(true);
    setRenderProg({ progress: 0, total: formats.length });
    try {
      const { data } = await api.post(`/snippets/clips/${clip.clip_id}/render`, { formats });
      await pollJob(data.job_id, (j) => setRenderProg({ progress: j.progress, total: j.total }));
      toast.success("Render complete — your social videos are ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Render failed");
    } finally {
      setRendering(false);
      setRenderProg(null);
      onChanged();
    }
  };

  const download = async (kind, ext) => {
    setDownloading(kind);
    try {
      const r = await api.get(`/snippets/clips/${clip.clip_id}/download`, { params: { kind }, responseType: "blob" });
      downloadBlob(r.data, `${(clip.title || "clip").replace(/[^\w -]/g, "").trim() || "clip"}_${kind}.${ext}`);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading("");
    }
  };

  const remove = async () => {
    if (!(await appConfirm("Its rendered videos stay downloadable until deleted from storage.", { title: "Delete this clip?" }))) return;
    await api.delete(`/snippets/clips/${clip.clip_id}`);
    toast.success("Clip deleted");
    onChanged();
  };

  const renders = Object.entries(clip.renders || {});

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`clip-card-${clip.clip_id}`}>
      <div className="flex items-center gap-3 mb-3">
        <Clapperboard size={15} className="text-brand-amber shrink-0" />
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title.trim() && title !== clip.title && patch({ title }, "Title saved")}
          className="type-list-title flex-1 min-w-0 bg-transparent text-ink-primary border-b border-transparent focus:border-line outline-none"
          data-testid="clip-title-input" />
        <span className="type-timestamp text-ink-muted shrink-0">{fmtTime(clip.start)} → {fmtTime(clip.end)} · {fmtTime(clip.duration)}</span>
        <StatusPill status={CLIP_STATUS[clip.status] || "draft"}>{clip.status}</StatusPill>
        <button onClick={remove} className="text-ink-muted hover:text-red-400" data-testid="clip-delete-btn"><Trash2 size={14} /></button>
      </div>

      {clip.audio_url && (
        <audio controls preload="none" src={`${APP_ORIGIN}${clip.audio_url}`} className="w-full h-8 mb-3" data-testid="clip-audio-preview" />
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          value={clip.template}
          onChange={(e) => patch({ template: e.target.value }, "Template updated")}
          className="type-button-label rounded-md border border-line bg-bg-base px-2 py-1.5 text-ink-primary"
          data-testid="clip-template-select">
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div className="flex items-center gap-1">
          <input type="number" step="0.5" value={trim.start} onChange={(e) => setTrim((p) => ({ ...p, start: e.target.value }))}
            className="w-20 rounded-md border border-line bg-bg-base px-2 py-1.5 type-button-label text-ink-primary" data-testid="clip-trim-start" />
          <span className="type-timestamp text-ink-muted">→</span>
          <input type="number" step="0.5" value={trim.end} onChange={(e) => setTrim((p) => ({ ...p, end: e.target.value }))}
            className="w-20 rounded-md border border-line bg-bg-base px-2 py-1.5 type-button-label text-ink-primary" data-testid="clip-trim-end" />
          {(Number(trim.start) !== clip.start || Number(trim.end) !== clip.end) && (
            <button onClick={() => patch({ start: Number(trim.start), end: Number(trim.end) }, "Clip re-cut")} disabled={busy}
              className="type-button-label rounded-md border border-amber-500/40 px-2 py-1.5 text-brand-amber hover:bg-amber-500/10 disabled:opacity-40"
              data-testid="clip-trim-apply">
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Re-cut"}
            </button>
          )}
        </div>
        <button
          onClick={() => { setShowSubs((v) => !v); if (subs === null) setSubs((clip.subtitles || []).map((s) => ({ ...s }))); }}
          className="type-button-label flex items-center gap-1 rounded-md border border-line px-2 py-1.5 text-ink-primary hover:bg-white/5"
          data-testid="clip-subs-toggle">
          <Captions size={13} /> Subtitles ({(clip.subtitles || []).length})
        </button>
      </div>

      {showSubs && subs && (
        <div className="mb-3 rounded-md border border-line/40 p-3" data-testid="clip-subs-editor">
          <div className="max-h-48 overflow-y-auto space-y-1.5 mb-2">
            {subs.length === 0 && <div className="type-list-subtitle text-ink-muted">No subtitles — transcribe the source media to auto-fill them.</div>}
            {subs.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="type-timestamp text-ink-muted w-20 shrink-0">{fmtTime(s.start)}–{fmtTime(s.end)}</span>
                <input value={s.text}
                  onChange={(e) => setSubs((p) => p.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                  className="flex-1 rounded border border-line/40 bg-bg-base px-2 py-1 type-list-subtitle text-ink-primary" />
              </div>
            ))}
          </div>
          {subs.length > 0 && (
            <button onClick={() => patch({ subtitles: subs }, "Subtitles saved")} disabled={busy}
              className="type-button-label rounded-md border border-line px-2.5 py-1 text-ink-primary hover:bg-white/5 disabled:opacity-40"
              data-testid="clip-subs-save">
              Save subtitles
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-line/40 pt-3">
        {Object.keys(FORMAT_LABEL).map((f) => (
          <label key={f} className="flex items-center gap-1.5 cursor-pointer type-button-label text-ink-primary">
            <input type="checkbox" checked={fmts[f]} onChange={(e) => setFmts((p) => ({ ...p, [f]: e.target.checked }))}
              className="accent-amber-500" data-testid={`clip-render-fmt-${f}`} />
            {FORMAT_LABEL[f]}
          </label>
        ))}
        <button onClick={render} disabled={rendering || clip.status === "cutting"}
          className="type-button-label ml-auto flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-1.5 text-bg-base disabled:opacity-40"
          data-testid="clip-render-btn">
          {rendering ? <Loader2 size={13} className="animate-spin" /> : null}
          {rendering
            ? `Rendering ${renderProg ? `${renderProg.progress}/${renderProg.total}` : "…"}`
            : `Render video · ${renderCost} cr`}
        </button>
      </div>

      {renders.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {renders.map(([f, r]) => (
            <div key={f} className="rounded-md border border-line/40 p-2" data-testid={`clip-render-${f}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="type-status-badge font-mono uppercase tracking-wider text-ink-muted">{FORMAT_LABEL[f] || f}</span>
                <button onClick={() => download(f, "mp4")} disabled={!!downloading}
                  className="type-button-label flex items-center gap-1 text-brand-amber hover:opacity-80 disabled:opacity-40"
                  data-testid={`clip-download-${f}`}>
                  {downloading === f ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} MP4
                </button>
              </div>
              <video controls preload="metadata" src={`${APP_ORIGIN}${r.url}`} className="w-full max-h-56 rounded bg-black" />
            </div>
          ))}
        </div>
      )}

      {clip.audio_path && (
        <button onClick={() => download("audio", "mp3")} disabled={!!downloading}
          className="type-button-label mt-2 flex items-center gap-1 text-ink-muted hover:text-ink-primary disabled:opacity-40"
          data-testid="clip-download-audio">
          <Download size={12} /> Download audio (MP3)
        </button>
      )}
    </div>
  );
}
